// types/commonSchedule.ts

import { Schedule, ScheduleMember, ScheduleVehicle } from './schedule';
import { OtherSchedule, OtherScheduleMember, OtherScheduleVehicle } from './otherSchedule';
import { RouteInfo } from './address';

export interface CombinedScheduleItem {
    id: number;
    type: 'construction' | 'other';
    scheduleDate: string;
    startTime?: string;
    endTime?: string;
    meetingTime?: string;
    isAllDay: boolean;
    notes?: string;
    teamType?: 'equipment' | 'scaffolding';
    requiredWorkers?: number;

    // 工事予定固有の情報
    constructionId?: number;
    siteName?: string;

    // その他予定固有の情報
    customerId?: number;
    customerName?: string;
    scheduleContent?: string;

    // 共通情報
    memberData?: ScheduleMember[] | OtherScheduleMember[];  // 型をより明確に
    vehicleData?: ScheduleVehicle[] | OtherScheduleVehicle[]; // 型をより明確に

    // 表示用の補助情報
    title?: string;      // カレンダー表示用のタイトル
    color?: string;      // 表示用の色情報
}

// 未報告スケジュール項目の型定義
export interface UnreportedScheduleItem extends Omit<Schedule, 'id'> {
    id: number; // オプショナルではなく必須に
    type: 'construction'; // 工事スケジュールのみなので固定
    constructionDetails?: string | null; // 工事詳細情報
    memberData?: ScheduleMember[]; // ScheduleMember型を明示的に指定
    vehicleData?: ScheduleVehicle[]; // ScheduleVehicle型を明示的に指定
    routeInfo?: RouteInfo | null; // address.tsから流用
}

// スケジュール-日報のリンク情報
export interface ScheduleReportLink {
    scheduleId: number;
    reportId: number;
}

// 日報メンバーデータに変換するための拡張インターフェース
export interface ReportMemberData {
    memberOrder: number;
    memberName: string; // 「所属 - 名前」形式または自社メンバーの場合は LastName
    isAllDate: number;
    workType: number;
    overtimeHours: number;
    isCountTarget: number;
    categoryId?: number | null; // 単価カテゴリID
    baseUnitPrice: number;
    calculatedUnitPrice: number;
    overtimePay: number;
    nightShiftPay: number;
}

// 変換ユーティリティ関数
export function convertToCommonFormat(
    item: Schedule | OtherSchedule,
    type: 'construction' | 'other'
): CombinedScheduleItem {
    if (type === 'construction') {
        const schedule = item as Schedule;
        return {
            id: schedule.id!,
            type: 'construction',
            scheduleDate: schedule.scheduleDate,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            meetingTime: schedule.meetingTime,
            isAllDay: schedule.isAllDay,
            notes: schedule.notes,
            teamType: schedule.teamType,
            requiredWorkers: schedule.requiredWorkers,
            constructionId: schedule.constructionId,
            siteName: schedule.siteName,
            customerName: schedule.customerName,
            memberData: schedule.memberData,
            vehicleData: schedule.vehicleData,
            title: schedule.siteName || '工事', // カレンダー表示用
            color: '#4285F4'  // 工事予定用の色（青）
        };
    } else {
        const otherSchedule = item as OtherSchedule;
        return {
            id: otherSchedule.id!,
            type: 'other',
            scheduleDate: otherSchedule.scheduleDate,
            startTime: otherSchedule.startTime,
            endTime: otherSchedule.endTime,
            meetingTime: otherSchedule.meetingTime,
            isAllDay: otherSchedule.isAllDay,
            notes: otherSchedule.notes,
            teamType: otherSchedule.teamType,
            requiredWorkers: otherSchedule.requiredWorkers,
            customerId: otherSchedule.customerId,
            customerName: otherSchedule.customerName,
            scheduleContent: otherSchedule.scheduleContent,
            memberData: otherSchedule.memberData,
            vehicleData: otherSchedule.vehicleData,
            title: otherSchedule.scheduleContent || 'その他予定', // カレンダー表示用
            color: '#EA4335'  // その他予定用の色（赤）
        };
    }
}

// スケジュールメンバーを日報メンバーに変換する関数
export function convertScheduleMemberToReportMember(
    member: ScheduleMember
  ): ReportMemberData {
    let memberName = '';
    
    // メンバー名を設定（自社メンバーまたは外注メンバー）
    if (member.Member_ID && member.LastName) {
      // 自社メンバーの場合
      memberName = member.LastName;
    } else if (member.VendorMember_ID) {
      // 外注メンバーの場合はフォーマット済みの名前を使用
      if (member.FormattedVendorMemberName) {
        memberName = member.FormattedVendorMemberName;
      } else if (member.VendorName && member.VendorMemberName) {
        // フォーマット済み名前がなければ自動生成
        memberName = `${member.VendorName} - ${member.VendorMemberName}`;
      } else {
        memberName = member.VendorMemberName || '不明なメンバー';
      }
    }
    
    return {
      memberOrder: member.Member_Order || 0,
      memberName: memberName,
      isAllDate: 1,
      workType: 0,
      overtimeHours: 0,
      isCountTarget: 1.00,
      categoryId: member.CategoryID,
      baseUnitPrice: 0, // 後で計算
      calculatedUnitPrice: 0,
      overtimePay: 0,
      nightShiftPay: 0
    };
  }
  

// 表示用フォーマット関数
export function formatScheduleForDisplay(item: CombinedScheduleItem): string {
    let displayText = '';

    // タイトル部分
    if (item.type === 'construction') {
        displayText = item.siteName || '工事';
    } else {
        displayText = item.scheduleContent || 'その他予定';
    }

    // 顧客名があれば追加
    if (item.customerName) {
        displayText += ` (${item.customerName})`;
    }

    // 時間情報
    if (!item.isAllDay && item.startTime) {
        const startTime = item.startTime.substring(0, 5); // HH:MM形式に
        const endTime = item.endTime ? item.endTime.substring(0, 5) : '';

        if (endTime) {
            displayText += ` ${startTime}〜${endTime}`;
        } else {
            displayText += ` ${startTime}〜`;
        }
    }

    return displayText;
}